import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { newDeliveryReference, reportDeliveryDiagnostic } from "@/lib/ads-manager-delivery-diagnostic";
import type { DeliveryReason } from "@shared/ads-manager-delivery-diagnostic";

export const deliveryBlockedMessage = (reason: DeliveryReason | null): string | null =>
  reason === "pending_update" ? "Another update is in progress. Wait for it to finish."
    : reason === "pending_navigation" ? "Loading campaigns… Wait for the list to finish loading."
      : reason === "recent_launch_only" ? "Current campaign status is unavailable. Refresh status or inspect this campaign in Meta Ads Manager."
        : reason === "historical_reporting" ? "Saved reporting does not confirm current Meta delivery. Open Meta Ads Manager to make delivery changes."
        : reason === "scope_changed" ? "The selected account or reporting range changed. Select the campaign again."
        : reason ? "Reporting is unavailable. Refresh the page before changing delivery." : null;

export function DeliveryControl({ entity, brandId, accountId, blocked, request }: {
  entity: { id: string; level: string; name: string; configuredStatus: string; deliveryReadiness?: string };
  brandId: string; accountId: string; blocked: DeliveryReason | null; request: () => void;
}) {
  const hintId = useId();
  const last = useRef<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const status = entity.configuredStatus === "ACTIVE" ? "PAUSED" : "ACTIVE";
  const readOnly = blocked === "historical_reporting";
  const hint = readOnly ? "Read-only reporting. Manage delivery in Meta Ads Manager." : deliveryBlockedMessage(blocked);
  useEffect(() => {
    const key = blocked ? `${brandId}:${accountId}:${entity.id}:${status}:${blocked}` : null;
    if (last.current === key) return;
    last.current = key;
    // Historical reporting is an expected read-only state, not a failed action.
    if (!blocked || blocked === "historical_reporting") { setReference(null); return; }
    const ref = newDeliveryReference();
    setReference(ref);
    reportDeliveryDiagnostic({ reference: ref, reason: blocked, stage: "interaction",
      brandId, accountId, entityId: entity.id, level: entity.level, requestedStatus: status,
      readiness: entity.deliveryReadiness, pending: blocked.startsWith("pending"), dispatchState: "not_sent", durationMs: 0 });
  }, [blocked, brandId, accountId, entity.id, entity.level, entity.deliveryReadiness, status]);
  return <div className="mt-2 max-w-[260px] whitespace-normal" title={readOnly ? hint! : undefined}>
    <Button size="sm" variant="outline" disabled={!!blocked} aria-describedby={blocked ? hintId : undefined}
      aria-label={`${status === "ACTIVE" ? "Resume" : "Pause"} ${entity.name}`} onClick={request}>
      {status === "ACTIVE" ? "Resume" : "Pause"} campaign
    </Button>
    {blocked && <p id={hintId} className={readOnly ? "sr-only" : "mt-1 text-xs text-muted-foreground"}>{hint}</p>}
    {blocked === "recent_launch_only" && <a className="mt-1 inline-block text-xs underline"
      href={`https://www.facebook.com/adsmanager/manage/campaigns?act=${encodeURIComponent(accountId)}&selected_campaign_ids=${encodeURIComponent(entity.id)}`}
      target="_blank" rel="noopener noreferrer">Inspect this campaign in Meta</a>}
    {blocked && !readOnly && reference && <DeliveryErrorDetails reference={reference} />}
  </div>;
}

export function DeliveryErrorDetails({ reference }: { reference: string }) {
  const [copied, setCopied] = useState(false);
  return <details className="mt-1 text-xs"><summary>Error details</summary>
    <p>Reference: <code>{reference}</code></p>
    <Button size="sm" variant="ghost" onClick={() => {
      void navigator.clipboard?.writeText(reference).then(() => setCopied(true)).catch(() => setCopied(false));
    }}>{copied ? "Copied" : "Copy reference"}</Button>
  </details>;
}
